/**
 * Trunnion AI - Version History Panel
 * Right sidebar panel showing a list of workflow versions with version number,
 * date, author, and a "Compare" button for each entry.
 * Uses mock version data for now.
 */

import { useState } from 'react';
import { GitBranch, GitCompare, Eye, User, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import type { WorkflowDefinition } from '@/types';

export interface WorkflowVersion {
  id: string;
  version: number;
  createdAt: string;
  author: string;
  message: string;
  isCurrent: boolean;
  snapshot: WorkflowDefinition;
}

interface VersionHistoryProps {
  onCompare: (versionA: WorkflowVersion, versionB: WorkflowVersion) => void;
  onRestore?: (version: WorkflowVersion) => void;
}

/** Mock version history data for development */
const MOCK_VERSIONS: WorkflowVersion[] = [
  {
    id: 'v5',
    version: 5,
    createdAt: '2026-02-22T14:30:00Z',
    author: 'Caleb Cobos',
    message: 'Added threat classification agent',
    isCurrent: true,
    snapshot: {
      id: 'wf-001',
      name: 'Threat Analysis Pipeline',
      description: 'Multi-stage threat analysis workflow',
      version: 5,
      classification: 'SECRET',
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 200 }, data: { nodeType: 'trigger', label: 'Intel Trigger', triggerType: 'webhook', inputSchema: {} } },
        { id: 'agent-1', type: 'agent', position: { x: 350, y: 200 }, data: { nodeType: 'agent', label: 'OSINT Collector', agentId: 'ag-01', agentName: 'OSINT Collector', tier: 'strategic', mode: 'autonomous', classification: 'SECRET', toolCount: 4, inputMapping: {}, outputMapping: {}, timeoutSeconds: 300, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'agent-2', type: 'agent', position: { x: 600, y: 200 }, data: { nodeType: 'agent', label: 'Threat Classifier', agentId: 'ag-02', agentName: 'Threat Classifier', tier: 'operational', mode: 'supervised', classification: 'SECRET', toolCount: 2, inputMapping: {}, outputMapping: {}, timeoutSeconds: 120, retryPolicy: { maxRetries: 1, backoffMultiplier: 1 } } },
        { id: 'terminal-1', type: 'terminal', position: { x: 850, y: 200 }, data: { nodeType: 'terminal', label: 'Complete', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'trigger-1', target: 'agent-1', type: 'dataFlow' },
        { id: 'e2', source: 'agent-1', target: 'agent-2', type: 'dataFlow' },
        { id: 'e3', source: 'agent-2', target: 'terminal-1', type: 'dataFlow' },
      ],
      createdAt: '2026-02-22T14:30:00Z',
      updatedAt: '2026-02-22T14:30:00Z',
      createdBy: 'caleb.cobos',
      isActive: true,
      tags: ['threat-analysis', 'osint'],
      metadata: {},
    },
  },
  {
    id: 'v4',
    version: 4,
    createdAt: '2026-02-21T10:15:00Z',
    author: 'Caleb Cobos',
    message: 'Updated OSINT agent timeout',
    isCurrent: false,
    snapshot: {
      id: 'wf-001',
      name: 'Threat Analysis Pipeline',
      description: 'Multi-stage threat analysis workflow',
      version: 4,
      classification: 'SECRET',
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 200 }, data: { nodeType: 'trigger', label: 'Intel Trigger', triggerType: 'webhook', inputSchema: {} } },
        { id: 'agent-1', type: 'agent', position: { x: 350, y: 200 }, data: { nodeType: 'agent', label: 'OSINT Collector', agentId: 'ag-01', agentName: 'OSINT Collector', tier: 'strategic', mode: 'autonomous', classification: 'SECRET', toolCount: 4, inputMapping: {}, outputMapping: {}, timeoutSeconds: 600, retryPolicy: { maxRetries: 3, backoffMultiplier: 2.0 } } },
        { id: 'terminal-1', type: 'terminal', position: { x: 600, y: 200 }, data: { nodeType: 'terminal', label: 'Complete', terminalType: 'success', outputMapping: {}, notifyOnComplete: true } },
      ],
      edges: [
        { id: 'e1', source: 'trigger-1', target: 'agent-1', type: 'dataFlow' },
        { id: 'e2', source: 'agent-1', target: 'terminal-1', type: 'dataFlow' },
      ],
      createdAt: '2026-02-21T10:15:00Z',
      updatedAt: '2026-02-21T10:15:00Z',
      createdBy: 'caleb.cobos',
      isActive: true,
      tags: ['threat-analysis', 'osint'],
      metadata: {},
    },
  },
  {
    id: 'v3',
    version: 3,
    createdAt: '2026-02-20T16:45:00Z',
    author: 'Sarah Mitchell',
    message: 'Added human approval gate',
    isCurrent: false,
    snapshot: {
      id: 'wf-001',
      name: 'Threat Analysis Pipeline',
      description: 'Basic threat analysis',
      version: 3,
      classification: 'SECRET',
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 200 }, data: { nodeType: 'trigger', label: 'Intel Trigger', triggerType: 'manual', inputSchema: {} } },
        { id: 'agent-1', type: 'agent', position: { x: 350, y: 200 }, data: { nodeType: 'agent', label: 'OSINT Collector', agentId: 'ag-01', agentName: 'OSINT Collector', tier: 'tactical', mode: 'supervised', classification: 'SECRET', toolCount: 3, inputMapping: {}, outputMapping: {}, timeoutSeconds: 300, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'gate-1', type: 'humanGate', position: { x: 600, y: 200 }, data: { nodeType: 'humanGate', label: 'Analyst Review', description: 'Review OSINT results', approverRoles: ['analyst'], timeoutMinutes: 60, classification: 'SECRET', requiresJustification: true } },
        { id: 'terminal-1', type: 'terminal', position: { x: 850, y: 200 }, data: { nodeType: 'terminal', label: 'Complete', terminalType: 'success', outputMapping: {}, notifyOnComplete: false } },
      ],
      edges: [
        { id: 'e1', source: 'trigger-1', target: 'agent-1', type: 'dataFlow' },
        { id: 'e2', source: 'agent-1', target: 'gate-1', type: 'dataFlow' },
        { id: 'e3', source: 'gate-1', target: 'terminal-1', type: 'dataFlow' },
      ],
      createdAt: '2026-02-20T16:45:00Z',
      updatedAt: '2026-02-20T16:45:00Z',
      createdBy: 'sarah.mitchell',
      isActive: true,
      tags: ['threat-analysis'],
      metadata: {},
    },
  },
  {
    id: 'v2',
    version: 2,
    createdAt: '2026-02-19T09:00:00Z',
    author: 'Caleb Cobos',
    message: 'Added OSINT collector agent',
    isCurrent: false,
    snapshot: {
      id: 'wf-001',
      name: 'Threat Analysis Pipeline',
      description: 'Basic threat analysis',
      version: 2,
      classification: 'UNCLASSIFIED',
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 200 }, data: { nodeType: 'trigger', label: 'Start', triggerType: 'manual', inputSchema: {} } },
        { id: 'agent-1', type: 'agent', position: { x: 350, y: 200 }, data: { nodeType: 'agent', label: 'OSINT Collector', agentId: 'ag-01', agentName: 'OSINT Collector', tier: 'tactical', mode: 'supervised', classification: 'UNCLASSIFIED', toolCount: 2, inputMapping: {}, outputMapping: {}, timeoutSeconds: 300, retryPolicy: { maxRetries: 2, backoffMultiplier: 1.5 } } },
        { id: 'terminal-1', type: 'terminal', position: { x: 600, y: 200 }, data: { nodeType: 'terminal', label: 'Done', terminalType: 'success', outputMapping: {}, notifyOnComplete: false } },
      ],
      edges: [
        { id: 'e1', source: 'trigger-1', target: 'agent-1', type: 'dataFlow' },
        { id: 'e2', source: 'agent-1', target: 'terminal-1', type: 'dataFlow' },
      ],
      createdAt: '2026-02-19T09:00:00Z',
      updatedAt: '2026-02-19T09:00:00Z',
      createdBy: 'caleb.cobos',
      isActive: true,
      tags: [],
      metadata: {},
    },
  },
  {
    id: 'v1',
    version: 1,
    createdAt: '2026-02-18T11:30:00Z',
    author: 'Caleb Cobos',
    message: 'Initial workflow creation',
    isCurrent: false,
    snapshot: {
      id: 'wf-001',
      name: 'Threat Analysis Pipeline',
      description: 'Initial draft',
      version: 1,
      classification: 'UNCLASSIFIED',
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 200 }, data: { nodeType: 'trigger', label: 'Start', triggerType: 'manual', inputSchema: {} } },
        { id: 'terminal-1', type: 'terminal', position: { x: 400, y: 200 }, data: { nodeType: 'terminal', label: 'End', terminalType: 'success', outputMapping: {}, notifyOnComplete: false } },
      ],
      edges: [
        { id: 'e1', source: 'trigger-1', target: 'terminal-1', type: 'dataFlow' },
      ],
      createdAt: '2026-02-18T11:30:00Z',
      updatedAt: '2026-02-18T11:30:00Z',
      createdBy: 'caleb.cobos',
      isActive: true,
      tags: [],
      metadata: {},
    },
  },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function VersionHistory({ onCompare, onRestore }: VersionHistoryProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const versions = MOCK_VERSIONS;
  const currentVersion = versions.find((v) => v.isCurrent);

  const handleCompare = (version: WorkflowVersion) => {
    if (!currentVersion) return;
    onCompare(version, currentVersion);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-trunnion-gold-500" />
        <h3 className="text-sm font-semibold text-white">Version History</h3>
        <Badge variant="default">{versions.length} versions</Badge>
      </div>

      <div className="space-y-2">
        {versions.map((version) => {
          const isSelected = selectedId === version.id;
          return (
            <Card
              key={version.id}
              padding="sm"
              hover
              className={`transition-all ${
                isSelected
                  ? 'border-trunnion-gold-500/50 bg-trunnion-gold-500/5'
                  : version.isCurrent
                    ? 'border-trunnion-slate-600'
                    : ''
              }`}
              onClick={() => setSelectedId(isSelected ? null : version.id)}
            >
              <div className="space-y-2">
                {/* Header row: version number + badges */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white">
                      v{version.version}
                    </span>
                    {version.isCurrent && (
                      <Badge variant="success" dot>
                        Current
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Commit message */}
                <p className="text-sm text-trunnion-slate-300">{version.message}</p>

                {/* Meta row: author + date */}
                <div className="flex items-center gap-3 text-xs text-trunnion-slate-500">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {version.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(version.createdAt)} {formatTime(version.createdAt)}
                  </span>
                </div>

                {/* Actions (visible when selected) */}
                {isSelected && (
                  <div className="flex items-center gap-2 pt-1 border-t border-trunnion-slate-700/50 mt-1">
                    {!version.isCurrent && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<GitCompare className="h-3.5 w-3.5" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompare(version);
                          }}
                        >
                          Compare
                        </Button>
                        {onRestore && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Eye className="h-3.5 w-3.5" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRestore(version);
                            }}
                          >
                            Restore
                          </Button>
                        )}
                      </>
                    )}
                    {version.isCurrent && (
                      <span className="text-xs text-trunnion-slate-500 italic">
                        This is the current version
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/** Export mock data for use in VersionCompareModal */
export { MOCK_VERSIONS };
